import { getWafEventAttackTypes, getWafEventStatus } from "./wafEventListUtils";
import { filterSignatureWafRawEventForDrawer } from "./eventDrawerData";
import { LAYERD_SIGNATURE_WAF_LABEL } from "./wafSourceLabels";

function splitUri(uri) {
  if (!uri) return { path: "—", query: null };
  const i = String(uri).indexOf("?");
  if (i === -1) return { path: uri, query: null };
  return { path: uri.slice(0, i), query: uri.slice(i + 1) };
}

function formatAction(action) {
  const a = String(action || "").toLowerCase();
  if (a === "block" || a === "blocked") return "Block";
  if (a === "allow" || a === "pass" || a === "passed") return "Allow";
  if (a === "log" || a === "monitor" || a === "detect") return "Log";
  if (a === "challenge" || a === "challenged") return "Challenge";
  return action || "—";
}

function formatResponseStatus(actionTaken) {
  const a = String(actionTaken || "").toLowerCase();
  if (a === "challenge" || a === "challenged") {
    return { message: "Request was challenged", color: "#7C3AED" };
  }
  if (a === "block" || a === "blocked") {
    return { message: "Request was blocked", color: "#B91C1C" };
  }
  if (a === "detect" || a === "log" || a === "monitor") {
    return { message: "Request was logged", color: "#065F46" };
  }
  if (a === "allow" || a === "pass" || a === "passed") {
    return { message: "Request was allowed", color: "#065F46" };
  }
  if (actionTaken) {
    return { message: `Request was ${formatAction(actionTaken).toLowerCase()}`, color: "#065F46" };
  }
  return { message: "Request was allowed", color: "#065F46" };
}

function anomalyToRiskLevel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "unknown";
  if (n >= 10) return "high";
  if (n >= 5) return "medium";
  return "low";
}

function verdictFromRow(row) {
  if (row?.blocked === 1 || row?.blocked === true) return "malicious";
  const action = String(row?.action_taken || "").toLowerCase();
  if (action === "block" || action === "blocked") return "malicious";
  return "suspicious";
}

function attackSummaryText(attackType) {
  if (!attackType) return null;
  const label = String(attackType).replace(/_/g, " ");
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} attack detected`;
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (value != null && String(value).trim()) {
    return [String(value).trim()];
  }
  return [];
}

/** Map signature_waf GET response → SmartParse-style drawer view model. */
export function buildSignatureWafDrawerView(row) {
  if (!row) return null;

  const rep = row.ip_reputation || {};
  const { path, query } = splitUri(row.uri);
  const statusString = getWafEventStatus(row);
  const verdict = verdictFromRow(row);
  const riskLevel = anomalyToRiskLevel(row.anomaly_score);
  const attackTypes = getWafEventAttackTypes(row);
  const attackType = attackTypes[0] || "";
  const summary = attackSummaryText(attackType);

  return {
    requestId: row.request_id,
    statusString,
    verdict,
    riskLevel,
    attackType,
    attackTypes,
    summary,
    detectionSummary: {
      timestamp: row.ts,
      hostname: row.host,
      verdict: verdict.charAt(0).toUpperCase() + verdict.slice(1),
      riskLevel: riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1),
      action: formatAction(row.action_taken),
      wafType: LAYERD_SIGNATURE_WAF_LABEL,
      matchedRuleId: row.matched_rule_id,
      anomalyScore: row.anomaly_score,
      paranoiaLevel: row.paranoia_level,
    },
    matchDetails: {
      messages: normalizeStringArray(row.match_msgs),
      fields: normalizeStringArray(row.match_fields),
      values: normalizeStringArray(row.match_values),
    },
    requestDetails: {
      host: row.host,
      requestedUri: row.uri,
      method: row.method,
      path,
      query,
      httpProtocol: row.http_version || row.alpn || "—",
      sourceIp: row.ip,
      sourcePort: row.source_port,
      ja3Hash: row.fp,
      ja4: row.ja4,
      userAgent: row.user_agent,
      country: row.country,
      city: row.city,
      region: row.region,
      timezone: row.timezone,
      asn: row.asn,
    },
    responseDetails: {
      statusCode: row.status,
      action: formatAction(row.action_taken),
      ...formatResponseStatus(row.action_taken),
    },
    threatIntel: {
      totalReports: rep.totalReports ?? 0,
      abuseConfidenceScore: rep.abuseConfidenceScore ?? null,
      ipAddress: rep.ipAddress,
      countryCode: rep.countryCode,
      usageType: rep.usageType,
      isp: rep.isp,
    },
    rawEventFiltered: filterSignatureWafRawEventForDrawer(row),
  };
}

/** Minimal v3-like shape for ViewSimilarEventsPanel / filter navigation. */
export function signatureViewToFilterEvent(view) {
  if (!view) return null;
  const rd = view.requestDetails || {};
  const action = String(view.detectionSummary?.action || "").toLowerCase();
  return {
    attacker: {
      source_ip: rd.sourceIp,
      ip: rd.sourceIp,
      geo: { country_iso: rd.country },
    },
    target: {
      host: rd.host,
      uri: rd.requestedUri,
      path: rd.path,
      method: rd.method,
    },
    primary_intent: { intent_name: view.attackType },
    attack_types: view.attackTypes,
    assessment: { action: action === "block" ? "blocked" : action },
  };
}

/** Map signature drawer view → NetworkTargetPanel props. */
export function signatureViewToNetworkShape(view) {
  if (!view) return { attacker: null, target: null };
  const rd = view.requestDetails || {};
  const ti = view.threatIntel || {};
  return {
    attacker: {
      source_ip: rd.sourceIp,
      source_port: rd.sourcePort,
      geo: {
        country_iso: rd.country,
        city: rd.city,
        region: rd.region,
        timezone: rd.timezone,
      },
      fingerprints: {
        ja3: rd.ja3Hash,
        ja4: rd.ja4,
      },
      asn: rd.asn,
      reputation: {
        abuse_confidence_score: ti.abuseConfidenceScore,
        isp: ti.isp,
        usage_type: ti.usageType,
        country_code: ti.countryCode,
        total_reports: ti.totalReports,
      },
    },
    target: {
      host: rd.host,
      uri: rd.requestedUri,
      path: rd.path,
      method: rd.method,
      http_version: rd.httpProtocol,
      response_status: view.responseDetails?.statusCode,
      user_agent: rd.userAgent,
      query_string: rd.query,
    },
  };
}
